import React, { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "../../helpers/api";
import { ConversationSkeleton, TicketListSkeleton } from "../../components/skeleton/PageSkeletons";
import { downloadSupportAttachment, filesToPayload, supportFileAccept, validateSupportFiles } from "../../helpers/support";
import { showError, showSuccess } from "../../components/ToastNotifications";
import "../../pages/HelpSupport.css";
import { useAccess } from "../../components/AccessContext";

const statuses = ["Open", "In Progress", "Waiting for User", "Resolved", "Closed"];
const priorities = ["Low", "Normal", "High", "Urgent"];
const pageSize = 20;
const formatDate = (value) => value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const AdminSupportTickets = () => {
  const { can } = useAccess();
  const canChangeTicket = can("admin.support.change_status");
  const canReply = can("admin.support.create");
  const [tickets, setTickets] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState({ status: "", priority: "", search: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [page, setPage] = useState(1);
  const [ticket, setTicket] = useState(null);
  const [detailsError, setDetailsError] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const listRequest = useRef(0);
  const detailsRequest = useRef(0);

  const loadTickets = useCallback(async () => {
    const requestId = ++listRequest.current;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams(Object.entries({ ...filters, page: String(page), limit: String(pageSize) }).filter(([, value]) => value));
      const result = await apiRequest(`/api/admin/support-tickets?${params}`);
      if (requestId === listRequest.current) {
        setTickets(result.tickets || []);
        setAdmins(result.admins || []);
        setPagination(result.pagination || null);
      }
    } catch (requestError) {
      if (requestId === listRequest.current) setError(requestError.message || "Unable to load support tickets.");
    } finally {
      if (requestId === listRequest.current) setLoading(false);
    }
  }, [filters, page]);

  const loadDetails = useCallback(async (id) => {
    const requestId = ++detailsRequest.current;
    setDetailsLoading(true);
    setDetailsError("");
    setTicket(null);
    try {
      const result = await apiRequest(`/api/support/admin/tickets/${id}`);
      if (requestId === detailsRequest.current) setTicket(result.ticket);
    } catch (requestError) {
      if (requestId === detailsRequest.current) {
        setDetailsError(requestError.message || "Unable to load ticket details.");
        showError(requestError.message || "Unable to load ticket details.");
      }
    } finally {
      if (requestId === detailsRequest.current) setDetailsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTickets();
    return () => { listRequest.current += 1; };
  }, [loadTickets]);
  useEffect(() => {
    if (selectedId) loadDetails(selectedId);
    return () => { detailsRequest.current += 1; };
  }, [selectedId, loadDetails]);

  const updateTicket = async (values) => {
    if (!selectedId) return;
    setSaving(true);
    try {
      await apiRequest(`/api/support/admin/tickets/${selectedId}`, { method: "PATCH", body: JSON.stringify(values) });
      await Promise.all([loadTickets(), loadDetails(selectedId)]);
      showSuccess("Ticket updated.");
    } catch (requestError) {
      showError(requestError.message || "Unable to update ticket.");
    } finally {
      setSaving(false);
    }
  };

  const chooseFiles = (event) => {
    const nextFiles = Array.from(event.target.files || []);
    event.target.value = "";
    const validationError = validateSupportFiles(nextFiles, files);
    if (validationError) return showError(validationError);
    setFiles((current) => [...current, ...nextFiles]);
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!reply.trim()) return showError("Write a message before replying.");
    setSaving(true);
    try {
      const attachments = await filesToPayload(files);
      await apiRequest(`/api/support/admin/tickets/${selectedId}/replies`, {
        method: "POST", body: JSON.stringify({ message: reply.trim(), attachments })
      });
      setReply("");
      setFiles([]);
      await Promise.all([loadDetails(selectedId), loadTickets()]);
      showSuccess("Reply sent to the customer.");
    } catch (requestError) {
      showError(requestError.message || "Unable to send reply.");
    } finally {
      setSaving(false);
    }
  };

  const messageAuthor = (message) => message.isAdmin ? (message.authorName || "Socialyorix Support") : (ticket?.user?.name || "Customer");
  const pageCount = pagination?.totalPages || 1;
  const firstTicket = pagination?.total ? (page - 1) * pageSize + 1 : 0;
  const lastTicket = Math.min(page * pageSize, pagination?.total || 0);

  return (
    <section className="support-page admin-support">
      <header className="admin-support-heading">
        <div><p className="support-eyebrow">CUSTOMER CARE</p><h1>Support tickets</h1><p>Review incoming requests and keep customer conversations moving.</p></div>
        <span className="admin-support-total"><i className="ti ti-lifebuoy" aria-hidden="true" /> {(pagination?.total || 0).toLocaleString()} tickets</span>
      </header>
      {error && <div className="support-inline-error" role="alert">{error} <button type="button" onClick={loadTickets}>Try again</button></div>}
      <div className="admin-support-filters">
        <label className="admin-support-search"><i className="ti ti-search" aria-hidden="true" /><input aria-label="Search tickets" placeholder="Search ticket, subject, or category" value={filters.search} onChange={(event) => { setPage(1); setFilters((current) => ({ ...current, search: event.target.value })); }} /></label>
        <label><span className="visually-hidden">Filter by status</span><select value={filters.status} onChange={(event) => { setPage(1); setFilters((current) => ({ ...current, status: event.target.value })); }}><option value="">All statuses</option>{statuses.map((status) => <option key={status}>{status}</option>)}</select></label>
        <label><span className="visually-hidden">Filter by priority</span><select value={filters.priority} onChange={(event) => { setPage(1); setFilters((current) => ({ ...current, priority: event.target.value })); }}><option value="">All priorities</option>{priorities.map((priority) => <option key={priority}>{priority}</option>)}</select></label>
      </div>
      <div className="admin-support-layout">
        <section className="support-card admin-support-list">
          <div className="admin-support-list-heading"><h2>Inbox</h2><span>{tickets.length} shown of {(pagination?.total || 0).toLocaleString()}</span></div>
          {loading ? <TicketListSkeleton rows={6} label="Loading tickets" /> : tickets.length ? <div className="admin-support-ticket-list">{tickets.map((item) => <button type="button" key={item.id} className={`admin-support-ticket${selectedId === item.id ? " is-selected" : ""}`} onClick={() => setSelectedId(item.id)}>
            <span className="support-ticket-row-top"><strong>{item.ticketNumber}</strong><StatusBadge status={item.status} /></span><strong className="admin-support-subject">{item.subject}</strong><span className="admin-support-customer">{item.user?.name} · {item.user?.email}</span><span className="support-ticket-meta">{item.category} <span>·</span> <PriorityBadge priority={item.priority} /></span><small>{formatDate(item.updatedAt)}</small>
          </button>)}</div> : <div className="support-empty"><strong>No tickets found</strong><span>Try changing your filters.</span></div>}
          {!loading && tickets.length > 0 && <div className="admin-support-pagination">
            <small>Showing {firstTicket}–{lastTicket} of {pagination?.total || 0}</small>
            <div><button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1 || loading}>Previous</button><span>Page {page} of {pageCount}</span><button type="button" onClick={() => setPage((current) => Math.min(pageCount, current + 1))} disabled={page >= pageCount || loading}>Next</button></div>
          </div>}
        </section>

        <section className="support-card admin-support-detail">
          {!selectedId ? <div className="support-empty admin-support-prompt"><span className="support-empty-icon"><i className="ti ti-message-circle" /></span><strong>Select a ticket</strong><span>Customer details and conversation history will appear here.</span></div> :
            detailsLoading ? <ConversationSkeleton /> :
            detailsError ? <div className="support-inline-error" role="alert">{detailsError} <button type="button" onClick={() => loadDetails(selectedId)}>Try again</button></div> :
            !ticket ? <div className="support-empty admin-support-prompt"><strong>Ticket unavailable</strong><span>Select another ticket or refresh the inbox.</span></div> : <>
              <div className="admin-support-detail-heading"><div><p className="support-eyebrow">{ticket.ticketNumber}</p><h2>{ticket.subject}</h2><p>{ticket.user?.name || "Customer"} · {ticket.category} · Created {formatDate(ticket.createdAt)}</p></div></div>
              <div className="admin-support-controls">
                <label>Status<select disabled={saving || !canChangeTicket} value={ticket.status} onChange={(event) => updateTicket({ status: event.target.value })}>{statuses.map((status) => <option key={status}>{status}</option>)}</select></label>
                <label>Priority<select disabled={saving || !canChangeTicket} value={ticket.priority} onChange={(event) => updateTicket({ priority: event.target.value })}>{priorities.map((priority) => <option key={priority}>{priority}</option>)}</select></label>
                <label>Assigned to<select disabled={saving || !canChangeTicket} value={ticket.assignedTo || ""} onChange={(event) => updateTicket({ assignedTo: event.target.value ? Number(event.target.value) : null })}><option value="">Unassigned</option>{admins.map((admin) => <option key={admin.id} value={admin.id}>{admin.name}</option>)}</select></label>
              </div>
              <div className="admin-support-history">
                <h3>Conversation</h3>
                {(ticket.replies || []).map((message) => <article className={`support-message${message.isAdmin ? " is-agent" : ""}`} key={message.id}>
                  <div className="support-message-top"><strong>{messageAuthor(message)}</strong><time>{formatDate(message.createdAt)}</time></div><p>{message.message}</p>
                  {message.attachments?.length > 0 && <div className="support-message-attachments">{message.attachments.map((attachment) => <button type="button" key={attachment.id} onClick={() => downloadSupportAttachment(attachment).catch((downloadError) => showError(downloadError.message))}><i className="ti ti-paperclip" />{attachment.name}</button>)}</div>}
                </article>)}
              </div>
              <div className="admin-support-status-history"><h3>Activity</h3>{ticket.history?.length ? <ol>{ticket.history.map((event) => <li key={event.id}><span>{event.eventType === "created" ? "Ticket created" : event.eventType === "reply" ? event.newValue : `${event.eventType === "assignment" ? "Assignment" : event.eventType} changed${event.oldValue ? ` from ${event.oldValue}` : ""}${event.newValue ? ` to ${event.newValue}` : ""}`}</span><time>{formatDate(event.createdAt)}</time></li>)}</ol> : <p>No history yet.</p>}</div>
              {canReply && !["Resolved", "Closed"].includes(ticket.status) && <form className="support-reply-form admin-support-reply" onSubmit={sendReply}>
                <label htmlFor="admin-support-reply">Reply to customer</label><textarea id="admin-support-reply" rows="4" maxLength="10000" value={reply} onChange={(event) => setReply(event.target.value)} placeholder="Write a response..." required />
                <input aria-label="Add attachments to reply" type="file" accept={supportFileAccept} multiple onChange={chooseFiles} />
                {files.length > 0 && <div className="support-reply-files">{files.map((file, index) => <span key={`${file.name}-${index}`}>{file.name}<button type="button" onClick={() => setFiles((current) => current.filter((_, fileIndex) => fileIndex !== index))} aria-label={`Remove ${file.name}`}><i className="ti ti-x" /></button></span>)}</div>}
                <button className="support-button support-button-primary" type="submit" disabled={saving}>{saving ? "Sending..." : "Send reply"} <i className="ti ti-send" aria-hidden="true" /></button>
              </form>}
            </>}
        </section>
      </div>
    </section>
  );
};

const StatusBadge = ({ status }) => <span className={`support-status support-status-${String(status || "").toLowerCase().replaceAll(" ", "-")}`}>{status}</span>;
const PriorityBadge = ({ priority }) => <span className={`support-priority support-priority-${String(priority || "").toLowerCase()}`}>{priority}</span>;

export default AdminSupportTickets;
